// =============================================================================
// src/lib/noShowRisk.js - no-show risk and appointment confirmation helpers.
//
//   useNoShowRisk(from, to)      -> { byId, refresh }  (noshow_risk RPC)
//       byId[appointment_id] = { score, tier, reasons, is_confirmed }
//   isConfirmed(appt)            one test for "confirmed" everywhere
//   confirmAppointment(appt)     staff marks a visit confirmed (audited)
//   markNoShow(appt)             staff marks a visit No Show (audited)
//   RISK_TIER                    label + badge variant per tier
//
// The score is rules with reasons, computed in the database (migration
// 20261001220000_noshow_risk_eod_sweep): past no-shows, late cancels, not
// confirmed, booked far ahead, new patient, no mobile number, Monday/Friday,
// early/late slot. No demographics, payer or diagnosis.
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { updateRow } from "./db";
import { DEFAULT_TZ, todayYmd, addDaysToYmd } from "./practiceDay";

export const RISK_TIER = {
  high: { label: "High no-show risk", short: "High risk", variant: "red" },
  medium: { label: "Medium no-show risk", short: "Med risk", variant: "amber" },
  low: { label: "Low no-show risk", short: "Low risk", variant: "neutral" },
};

export function isConfirmed(a) {
  if (!a) return false;
  return a.status === "Confirmed" || a.confirmation_status === "Confirmed" || a.confirmation_status === "Patient Confirmed";
}

// Round 7 bug 6: "today" for the risk range is the PRACTICE's calendar day
// (practiceDay.js), not the device's. The old version read the device clock,
// so a laptop set to another zone asked for another day's risk, and
// scripts/check-utc-today.mjs cannot see that pattern. The name stays because
// the dashboards and Before you go import it; pass the practice zone when the
// caller has it (usePracticeToday().tz), else Eastern, as practiceDay does.
export function localISO(d = new Date(), tz = DEFAULT_TZ) {
  return todayYmd(tz || DEFAULT_TZ, d);
}

// Calendar arithmetic on a "YYYY-MM-DD" (no zone shift).
export function addDaysISO(iso, n) {
  return addDaysToYmd(iso, n);
}

// Risk for every Scheduled / Confirmed visit between two dates (inclusive).
// Failures are non-fatal: the screens still work without badges.
//   auto: false skips the hook's own read on mount and on a date change, for
//   a screen that calls refresh() once its visits have loaded. The Schedule
//   did both and read noshow_risk three times on open (round 7 bug 6).
export function useNoShowRisk(from, to, enabled = true, { auto = true } = {}) {
  const [byId, setById] = useState({});
  const refresh = useCallback(async () => {
    if (!enabled || !from || !to) return;
    const { data, error } = await supabase.rpc("noshow_risk", { p_from: from, p_to: to });
    if (error) { console.warn("[noShowRisk] load failed:", error.message); return; }
    const m = {};
    (data || []).forEach((r) => { m[r.appointment_id] = r; });
    setById(m);
  }, [from, to, enabled]);
  useEffect(() => { if (auto) refresh(); }, [refresh, auto]);
  return { byId, refresh };
}

export async function confirmAppointment(appt) {
  return updateRow("appointments", appt.id,
    { confirmation_status: "Confirmed", confirmed_via: "Staff", confirmed_at: new Date().toISOString() },
    { audit: { entityType: "appointments", patientId: appt.patient_id || null, details: { event: "appointment_confirmed", via: "Staff" } } });
}

export async function markNoShow(appt) {
  return updateRow("appointments", appt.id, { status: "No Show" },
    { audit: { entityType: "appointments", patientId: appt.patient_id || null, details: { event: "marked_no_show", source: "end_of_day" } } });
}
